/**
 * Snapshots block for the report editor's Insights pane step ③
 * (PR-4 builder reorg) — rendered ABOVE `ExecutionsTable`.
 *
 * List bar (freshness chip + "Ejecutar watcher" + "Backfill…") + the
 * snapshot table with per-row Ver (detail drawer with per-insight
 * cards + feedback summary) and Regenerar (confirm → proxied LLM
 * re-render → list refresh).
 *
 * Compute actions are PROXIED to the portal — 503 ("Portal proxy not
 * configured…") / 502 ({portal_status, portal_detail}) surface
 * VERBATIM in the error banner via `formatAdminAiError` (Rule 1).
 */

import {
  CalendarDays,
  MessageSquareText,
  Play,
  RotateCcw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useT } from "@/i18n";
import {
  aiSnapshotsAdminApi,
  formatAdminAiError,
  formatDateTime,
  truncateText,
  type AIAnalysisSnapshot,
  type AIAnalysisSnapshotListItem,
  type BackfillStatusResponse,
  type FeedbackTally,
  type InsightFeedbackSummary,
} from "@/lib/admin-ai-api";

const BACKFILL_POLL_INTERVAL_MS = 2000;

interface SnapshotsPanelProps {
  draftKey: string;
}

export function SnapshotsPanel({ draftKey }: SnapshotsPanelProps): JSX.Element {
  const { t } = useT();

  const [rows, setRows] = useState<AIAnalysisSnapshotListItem[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [count, list] = await Promise.all([
        aiSnapshotsAdminApi.count(draftKey),
        aiSnapshotsAdminApi.list(draftKey, { limit: 50 }),
      ]);
      setTotal(count.count);
      setRows(list);
    } catch (err) {
      setError(formatAdminAiError(err));
    } finally {
      setLoading(false);
    }
  }, [draftKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleTrigger = async (): Promise<void> => {
    setTriggering(true);
    setError(null);
    setNotice(null);
    try {
      const res = await aiSnapshotsAdminApi.triggerWatcher();
      setNotice(t("snapshots.triggerDone", { count: res.created }));
      await load();
    } catch (err) {
      setError(formatAdminAiError(err));
    } finally {
      setTriggering(false);
    }
  };

  const handleRegenerate = async (snapshotId: string): Promise<void> => {
    if (!window.confirm(t("snapshots.regenConfirm"))) return;
    setRegeneratingId(snapshotId);
    setError(null);
    setNotice(null);
    try {
      await aiSnapshotsAdminApi.regenerate(draftKey, snapshotId);
      await load();
    } catch (err) {
      setError(formatAdminAiError(err));
    } finally {
      setRegeneratingId(null);
    }
  };

  const latestWeek = rows.length > 0 ? rows[0]!.week_iso : null;

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-neutral-900">
          {t("snapshots.title")}
          {total !== null ? (
            <span className="ml-2 text-xs font-normal text-neutral-500">
              ({total})
            </span>
          ) : null}
        </h2>
        {latestWeek && (
          <span className="inline-flex items-center gap-1 rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-xs font-medium text-success-dark">
            {t("snapshots.upToDate", { week: latestWeek })}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleTrigger()}
            disabled={triggering}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2.5 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            <Play size={12} className={triggering ? "animate-pulse" : ""} />
            {t("snapshots.trigger")}
          </button>
          <button
            type="button"
            onClick={() => setBackfillOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-primary-light px-2.5 py-1.5 text-xs font-medium text-primary-ink hover:bg-primary-soft"
          >
            <CalendarDays size={12} />
            {t("snapshots.backfill")}
          </button>
        </div>
      </header>
      <p className="mb-4 text-xs text-neutral-500">
        {t("snapshots.description")}
      </p>

      {notice && (
        <div className="mb-3 rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success-dark">
          {notice}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-error/40 bg-error/5 px-3 py-2 text-xs text-error-dark"
        >
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-neutral-500">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-neutral-500">{t("snapshots.empty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-neutral-500">
              <tr className="border-b border-neutral-200">
                <th className="px-2 py-1 text-left">
                  {t("snapshots.col.week")}
                </th>
                <th className="px-2 py-1 text-left">
                  {t("snapshots.col.summary")}
                </th>
                <th className="px-2 py-1 text-right">
                  {t("snapshots.col.insights")}
                </th>
                <th className="px-2 py-1 text-left">
                  {t("snapshots.col.feedback")}
                </th>
                <th className="px-2 py-1 text-right">
                  {t("snapshots.col.regen")}
                </th>
                <th className="px-2 py-1 text-right">
                  {t("snapshots.col.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-neutral-100">
                  <td className="whitespace-nowrap px-2 py-1.5 font-mono text-neutral-700">
                    {row.week_iso}
                  </td>
                  <td
                    className="max-w-md truncate px-2 py-1.5 text-neutral-600"
                    title={row.executive_summary_short}
                  >
                    {truncateText(row.executive_summary_short, 120)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-neutral-600">
                    {row.insights_count}
                  </td>
                  <td className="px-2 py-1.5">
                    <TallyInline tally={row.feedback_summary} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {row.regenerated_count > 0 ? (
                      <span className="inline-flex items-center rounded-full bg-warning/20 px-1.5 py-0.5 text-xs font-medium text-warning-dark">
                        ×{row.regenerated_count}
                      </span>
                    ) : (
                      <span className="text-neutral-300">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => setDetailId(row.id)}
                      className="rounded-md border border-neutral-200 px-2 py-0.5 text-xs text-neutral-700 hover:bg-neutral-50"
                    >
                      {t("snapshots.view")}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleRegenerate(row.id)}
                      disabled={regeneratingId !== null}
                      className="ml-1.5 inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-0.5 text-xs text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                    >
                      {regeneratingId === row.id ? (
                        <Spinner size={10} ariaLabel={t("snapshots.regenerate")} />
                      ) : (
                        <RotateCcw size={10} />
                      )}
                      {t("snapshots.regenerate")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailId && (
        <SnapshotDetailDrawer
          reportKey={draftKey}
          snapshotId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
      {backfillOpen && (
        <BackfillModal
          reportKey={draftKey}
          onClose={() => setBackfillOpen(false)}
          onComplete={() => void load()}
        />
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────
// Inline feedback tallies
// ───────────────────────────────────────────────────────────

function TallyInline({ tally }: { tally: FeedbackTally }): JSX.Element {
  const totalSignals = tally.up + tally.down + tally.comments;
  if (totalSignals === 0) {
    return <span className="text-neutral-300">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-2 text-xs text-neutral-600">
      {tally.up > 0 && (
        <span className="inline-flex items-center gap-0.5">
          <ThumbsUp size={10} className="text-success-dark" />
          {tally.up}
        </span>
      )}
      {tally.down > 0 && (
        <span className="inline-flex items-center gap-0.5">
          <ThumbsDown size={10} className="text-error-dark" />
          {tally.down}
        </span>
      )}
      {tally.comments > 0 && (
        <span className="inline-flex items-center gap-0.5 text-neutral-500">
          <MessageSquareText size={10} />
          {tally.comments}
        </span>
      )}
    </span>
  );
}

// ───────────────────────────────────────────────────────────
// Detail drawer — full snapshot + per-insight feedback
// ───────────────────────────────────────────────────────────

interface SnapshotDetailDrawerProps {
  reportKey: string;
  snapshotId: string;
  onClose: () => void;
}

function SnapshotDetailDrawer({
  reportKey,
  snapshotId,
  onClose,
}: SnapshotDetailDrawerProps): JSX.Element {
  const { t } = useT();
  const [snapshot, setSnapshot] = useState<AIAnalysisSnapshot | null>(null);
  const [feedback, setFeedback] = useState<InsightFeedbackSummary | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [s, f] = await Promise.all([
          aiSnapshotsAdminApi.get(reportKey, snapshotId),
          aiSnapshotsAdminApi.feedbackSummary(reportKey, snapshotId),
        ]);
        if (cancelled) return;
        setSnapshot(s);
        setFeedback(f);
      } catch (err) {
        if (!cancelled) setError(formatAdminAiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportKey, snapshotId]);

  const tallyByIndex = new Map<
    number,
    InsightFeedbackSummary["per_insight"][number]
  >();
  if (feedback) {
    for (const row of feedback.per_insight) tallyByIndex.set(row.index, row);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        className="h-full w-[720px] max-w-full overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-neutral-200 bg-white px-5 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles size={16} className="shrink-0 text-primary-dark" />
            <h3 className="truncate text-base font-semibold text-neutral-900">
              {snapshot
                ? `${snapshot.week_iso} · ${snapshot.as_of_date}`
                : t("common.loading")}
            </h3>
            {snapshot && snapshot.regenerated_count > 0 && (
              <span className="shrink-0 rounded-full bg-warning/20 px-2 py-0.5 text-xs font-medium text-warning-dark">
                {t("snapshots.detail.regenerated", {
                  count: snapshot.regenerated_count,
                })}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100"
            aria-label={t("common.close")}
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-5 px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Spinner size={20} ariaLabel={t("common.loading")} />
            </div>
          )}
          {error && (
            <div
              role="alert"
              className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark"
            >
              {error}
            </div>
          )}
          {snapshot && !loading && (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs text-neutral-700">
                <div>
                  <span className="font-medium text-neutral-500">model:</span>{" "}
                  <span className="font-mono">{snapshot.model}</span>
                </div>
                <div>
                  <span className="font-medium text-neutral-500">created:</span>{" "}
                  {formatDateTime(snapshot.created_at)}
                </div>
              </div>

              {Object.keys(snapshot.filters).length > 0 && (
                <section>
                  <h4 className="mb-1.5 text-xs font-semibold text-neutral-500">
                    {t("snapshots.detail.filters")}
                  </h4>
                  <pre className="overflow-x-auto rounded border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-800">
                    {JSON.stringify(snapshot.filters, null, 2)}
                  </pre>
                </section>
              )}

              {feedback &&
                feedback.overall.up +
                  feedback.overall.down +
                  feedback.overall.comments >
                  0 && (
                  <section className="rounded-lg border border-primary/30 bg-primary-soft/50 p-3">
                    <h4 className="mb-2 text-xs font-semibold text-primary-ink">
                      {t("snapshots.detail.feedback")}
                    </h4>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-700">
                      <span className="inline-flex items-center gap-1">
                        <ThumbsUp size={12} className="text-success-dark" />
                        <strong>{feedback.overall.up}</strong>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <ThumbsDown size={12} className="text-error-dark" />
                        <strong>{feedback.overall.down}</strong>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MessageSquareText
                          size={12}
                          className="text-neutral-500"
                        />
                        <strong>{feedback.overall.comments}</strong>{" "}
                        {t("snapshots.detail.comments")}
                      </span>
                    </div>
                  </section>
                )}

              <section>
                <div className="mb-1.5 flex items-start justify-between gap-3">
                  <h4 className="text-xs font-semibold text-neutral-500">
                    {t("snapshots.detail.execSummary")}
                  </h4>
                  <FeedbackChip tally={tallyByIndex.get(-1)} />
                </div>
                <p className="whitespace-pre-line text-sm leading-relaxed text-neutral-800">
                  {snapshot.executive_summary}
                </p>
                <FeedbackComments tally={tallyByIndex.get(-1)} />
              </section>

              <section className="space-y-3">
                <h4 className="text-xs font-semibold text-neutral-500">
                  {t("snapshots.detail.insights", {
                    count: snapshot.insights.length,
                  })}
                </h4>
                {snapshot.insights.map((insight, idx) => {
                  const tally = tallyByIndex.get(insight.index ?? idx);
                  return (
                    <div
                      key={idx}
                      className="rounded-lg border border-neutral-200 bg-white p-4"
                    >
                      <div className="mb-2 flex items-start gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-line text-xs font-bold text-primary-ink">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <h5 className="flex-1 pt-0.5 text-sm font-semibold text-neutral-900">
                          {insight.title}
                        </h5>
                        <FeedbackChip tally={tally} />
                      </div>
                      <p className="mb-2 text-sm text-neutral-700">
                        {insight.summary}
                      </p>
                      <p className="whitespace-pre-line border-t border-neutral-100 pt-2 text-xs text-neutral-600">
                        {insight.detail}
                      </p>
                      <FeedbackComments tally={tally} />
                    </div>
                  );
                })}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FeedbackChip({
  tally,
}: {
  tally: InsightFeedbackSummary["per_insight"][number] | undefined;
}): JSX.Element | null {
  if (!tally) return null;
  const { up, down } = tally;
  if (up === 0 && down === 0) return null;
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs text-neutral-700">
      {up > 0 && (
        <span className="inline-flex items-center gap-0.5">
          <ThumbsUp size={10} className="text-success-dark" />
          {up}
        </span>
      )}
      {down > 0 && (
        <span className="inline-flex items-center gap-0.5">
          <ThumbsDown size={10} className="text-error-dark" />
          {down}
        </span>
      )}
    </span>
  );
}

function FeedbackComments({
  tally,
}: {
  tally: InsightFeedbackSummary["per_insight"][number] | undefined;
}): JSX.Element | null {
  if (!tally || tally.comments.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5 border-t border-neutral-100 pt-2">
      {tally.comments.map((c, i) => (
        <div
          key={i}
          className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs ${
            c.rating === "down"
              ? "border-error/30 bg-error/5"
              : "border-success/30 bg-success/10"
          }`}
        >
          {c.rating === "down" ? (
            <ThumbsDown size={11} className="mt-0.5 shrink-0 text-error-dark" />
          ) : (
            <ThumbsUp size={11} className="mt-0.5 shrink-0 text-success-dark" />
          )}
          <span className="flex-1 italic text-neutral-700">
            &ldquo;{c.comment}&rdquo;
          </span>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────
// Backfill modal — start + live progress polling (2 s)
// ───────────────────────────────────────────────────────────

interface BackfillModalProps {
  reportKey: string;
  onClose: () => void;
  onComplete: () => void;
}

function BackfillModal({
  reportKey,
  onClose,
  onComplete,
}: BackfillModalProps): JSX.Element {
  const { t } = useT();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<BackfillStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const poll = useCallback(
    async (id: string): Promise<void> => {
      try {
        const next = await aiSnapshotsAdminApi.backfillStatus(reportKey, id);
        setStatus(next);
        if (next.status === "running") {
          pollingRef.current = setTimeout(
            () => void poll(id),
            BACKFILL_POLL_INTERVAL_MS,
          );
        } else {
          onComplete();
        }
      } catch (err) {
        setError(formatAdminAiError(err));
      }
    },
    [reportKey, onComplete],
  );

  const handleStart = async (): Promise<void> => {
    setError(null);
    setStarting(true);
    try {
      const body: { start?: string; end?: string } = {};
      if (start) body.start = start;
      if (end) body.end = end;
      const res = await aiSnapshotsAdminApi.startBackfill(reportKey, body);
      setJobId(res.job_id);
      void poll(res.job_id);
    } catch (err) {
      setError(formatAdminAiError(err));
    } finally {
      setStarting(false);
    }
  };

  const percent =
    status && status.total > 0
      ? Math.round((status.processed / status.total) * 100)
      : 0;
  const done = status?.status === "done";
  const failed = status?.status === "error";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="relative w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-neutral-200 px-5 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
            <CalendarDays size={16} className="text-primary-dark" />
            {t("snapshots.backfill.title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100"
            aria-label={t("common.close")}
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-xs text-neutral-600">
            {t("snapshots.backfill.help", { key: reportKey })}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-neutral-700">
                {t("snapshots.backfill.start")}
              </span>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                disabled={!!jobId}
                className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm disabled:opacity-50"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-neutral-700">
                {t("snapshots.backfill.end")}
              </span>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                disabled={!!jobId}
                className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm disabled:opacity-50"
              />
            </label>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-xs text-error-dark"
            >
              {error}
            </div>
          )}

          {status && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-neutral-600">
                  {done
                    ? t("snapshots.backfill.done", { created: status.created })
                    : failed
                      ? t("snapshots.backfill.failed", {
                          error: status.error ?? "?",
                        })
                      : t("snapshots.backfill.progress", {
                          processed: status.processed,
                          total: status.total,
                        })}
                </span>
                <span className="font-mono text-neutral-500">{percent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
                <div
                  className={`h-full transition-all ${
                    failed
                      ? "bg-error"
                      : done
                        ? "bg-success/100"
                        : "bg-primary"
                  }`}
                  style={{ width: `${Math.max(percent, failed ? 100 : 2)}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            {done || failed ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-primary-dark px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-ink"
              >
                {t("common.close")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={() => void handleStart()}
                  disabled={starting || !!jobId}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-dark px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-ink disabled:opacity-50"
                >
                  {starting || jobId ? (
                    <>
                      <Spinner
                        size={14}
                        color="currentColor"
                        ariaLabel={t("snapshots.backfill.running")}
                      />
                      {t("snapshots.backfill.running")}
                    </>
                  ) : (
                    <>
                      <Play size={12} />
                      {t("snapshots.backfill.run")}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
